import { useRef, useState } from 'react'

import { ZenModeLocationNameTypes } from '~/events/eventTypes'
import useTrackEvents from '~/events/trackers/useTrackEvents'
import { useZenModeQuery } from '~/graphql/generated/schema'
import { useLaunchDarklyFlags } from '~/hooks/useFeatureFlags'
import logger from '~/utils/logger'

const onZenModeApiError = (error: any) => logger.error('useZenMode:useZenModeQuery:Error', error)

export const useZenMode = () => {
  const { trackEvent } = useTrackEvents()
  const { ldFlags } = useLaunchDarklyFlags()
  const resp = useZenModeQuery({ fetchPolicy: 'cache-first', onError: onZenModeApiError })
  const isEventTrackedOnceRef = useRef(false)
  const [ldFeatureEnabled] = useState(() => ldFlags['zen_mode']) // the raw value is needed to track the event, null means the experiment is not running

  const zenData = resp?.data?.me?.gameVariant?.zen
  const isUnlocked = zenData?.isUnlocked ?? false
  const playCount = zenData?.gamePlayCountInPeriod ?? 0
  const playLimit = zenData?.gamePlayLimitPerPeriod ?? 0
  const isLimitExceeded = zenData?.gamePlaysExhausted ?? true
  const playDiff = (playLimit ?? 0) - (playCount ?? 0)
  const remainingPlays = playDiff <= 0 ? 0 : playDiff
  const featureEnabled = isUnlocked && ldFeatureEnabled

  const trackOnceZenModeDetails = ({ location }: { location: ZenModeLocationNameTypes }) => {
    if (resp.loading) return // if the data is not loaded yet, we should not track the event
    if (resp.error) return // if there is an error, we should not track the event as we don't know if the user is eligible for zen mode
    if (isEventTrackedOnceRef.current) return // this function usually will be called at rendering, multiple times, so we need to track the event only once
    isEventTrackedOnceRef.current = true
    trackEvent('zen_mode_detail', {
      ld_feature_flag: ldFeatureEnabled,
      is_unlocked: isUnlocked,
      location,
    })
  }

  return {
    loading: resp.loading,
    error: resp.error,
    featureEnabled,
    isUnlocked,
    playLimit,
    playCount,
    remainingPlays,
    isLimitExceeded,
    trackOnceZenModeDetails,
  }
}
